import NavbarV2 from '../../components/navbarV2/NavbarV2';
import './CreditNoteDetailV2.css';
import { useCallback, useEffect, useState } from 'react';
import CartList from '../../components/CartList/CartList';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PrintHero from '../../components/PrintHero/PrintHero';
import { FaFileAlt } from 'react-icons/fa';
import ListEmptyStateV2, { ListPageLoadingV2 } from '../../components/listEmptyStateV2/ListEmptyStateV2';
import { ClipLoader } from 'react-spinners';
import { printCreditNotePdf } from '../../pdfPrinting';
import { formatMoney, combinedAmount, toAmount, vat18FromCombined } from '../../utils/formatMoney';

const normalizeIkeaArticleId = (raw) => {
    const s = String(raw || '').trim();
    const stripped = s.replace(/^s/i, '');
    const digits = stripped.replace(/\D/g, '');
    return digits || stripped;
};

const CreditNoteDetailV2 = () => {
    const [old, setOld] = useState(false);
    const [order, setOrder] = useState(null);
    const [currentCreditNote, setCurrentCreditNote] = useState(null);
    const [admin, setAdmin] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [creditTotal, setCreditTotal] = useState(0);
    const [loadAttempted, setLoadAttempted] = useState(false);
    const [cartWithStock, setCartWithStock] = useState(null);
    const [stockLoading, setStockLoading] = useState(false);
    const [pdfGenerating, setPdfGenerating] = useState(false);
    const params = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        setAdmin(localStorage.getItem('admin') === 'true');
        if (params.id) {
            getOrder(params.id);
        } else {
            navigate('/');
        }
    }, [params, navigate]);

    useEffect(() => {
        if (!order) {
            return;
        }

        let cancelled = false;

        const loadStock = async () => {
            if (!order.cart?.length) {
                setCartWithStock([]);
                setStockLoading(false);
                return;
            }

            setCartWithStock(null);
            setStockLoading(true);

            const cart = order.cart;
            const ikeaIds = [
                ...new Set(
                    cart
                        .filter((item) => String(item.id).length <= 20)
                        .map((item) => normalizeIkeaArticleId(item.id)),
                ),
            ].filter(Boolean);

            let itemsMap = {};
            if (ikeaIds.length > 0) {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API}/api/ikea/stock`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ articleIds: ikeaIds }),
                    });
                    const data = await response.json();
                    if (data.items) {
                        itemsMap = data.items;
                    }
                } catch (error) {
                    console.error('Error fetching IKEA stock:', error);
                }
            }

            const items = cart.map((item) => {
                let stock = null;
                let probability = null;
                if (String(item.id).length <= 20) {
                    const key = normalizeIkeaArticleId(item.id);
                    const row = itemsMap[key];
                    if (row?.ok) {
                        stock = row.stock;
                        probability = row.probability;
                    }
                }
                return {
                    ...item,
                    stock,
                    probability,
                };
            });

            if (!cancelled) {
                setCartWithStock(items);
                setStockLoading(false);
            }
        };

        loadStock();
        return () => {
            cancelled = true;
        };
    }, [order?._id, order?.cart]);

    const getOrder = (id) => {
        const urlParams = new URLSearchParams(window.location.search);
        const creditNoteId = urlParams.get('creditNoteId');

        fetch(`${process.env.REACT_APP_API}/api/getOrder/${id}`, {})
            .then((response) => {
                if (response.status === 200) {
                    response.json().then((data) => {
                        setOrder(data);
                        if (data.createdAt !== undefined) setOld(true);
                        setLoadAttempted(true);
                        if (creditNoteId && data.creditNotes && data.creditNotes.length > 0) {
                            const creditNote = data.creditNotes.find((cn) => cn._id === creditNoteId);
                            if (creditNote) {
                                setCurrentCreditNote(creditNote);
                                calculateCreditTotal(creditNote.items);
                                return;
                            }
                        }
                        if (data.creditNotes && data.creditNotes.length > 0) {
                            const cn = data.creditNotes[0];
                            setCurrentCreditNote(cn);
                            calculateCreditTotal(cn.items);
                        } else {
                            setCurrentCreditNote(null);
                        }
                    });
                } else {
                    setLoadAttempted(true);
                }
            })
            .catch(() => setLoadAttempted(true));
    };

    const calculateCreditTotal = (cart) => {
        let total = 0;
        for (let i = 0; i < cart.length; i++) {
            const item = cart[i];
            total += item.amount * item.price;
        }
        setCreditTotal(total);
    };

    const handlePrintCreditNotePdf = useCallback(async () => {
        if (!order || !currentCreditNote) return;
        setPdfGenerating(true);
        try {
            await printCreditNotePdf(order, currentCreditNote);
            toast.success('Opening print dialog…');
        } catch (e) {
            toast.error(e?.message || 'Could not generate PDF.');
        } finally {
            setPdfGenerating(false);
        }
    }, [order, currentCreditNote]);

    window.onbeforeprint = () => {
        setPrinting(true);
    };

    window.onafterprint = () => {
        setPrinting(false);
    };

    const updatePayment = (e) => {
        e.preventDefault();

        fetch(`${process.env.REACT_APP_API}/api/changeCreditPayment/${currentCreditNote._id}`, {
            method: 'POST',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            body: JSON.stringify({
                surcharge: document.getElementById('surcharge').value,
                shipping: document.getElementById('shipping').value,
            }),
        }).then((response) => {
            if (response.status === 200) {
                toast.success('Changed payment details!');
                getOrder(params.id);
            } else if (response.status === 500) {
                toast.error('Server error.');
            }
        });
    };

    const getCombinedTotal = () =>
        Number(creditTotal) + Number(currentCreditNote?.surcharge || 0) + Number(currentCreditNote?.shipping || 0);

    const pay = () => {
        fetch(`${process.env.REACT_APP_API}/api/createPaymentOrderEndpoint`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                orderNumber: order.orderNumber,
                name: order.name,
                phone: order.phone,
                email: order.email,
                total: formatMoney(combinedAmount(order)),
                payLater: false,
                id: order._id,
            }),
        }).then((response) => {
            if (response.status === 200) {
                response.json().then((data) => {
                    window.location = `https://www.vivapayments.com/web/checkout?ref=${data.orderCode}&color=0A6CFF`;
                });
            } else if (response.status === 500) {
                toast.error('Server error.');
            }
        });
    };

    const baseCart = cartWithStock !== null ? cartWithStock : (order?.cart ?? []);
    const creditCartItems =
        order && currentCreditNote
            ? baseCart
                  .filter((x) => currentCreditNote.items.filter((y) => y.SKU === x.id).length > 0)
                  .map((x) => ({
                      ...x,
                      amount: currentCreditNote.items.filter((y) => y.SKU === x.id)[0].amount,
                  }))
            : [];

    const vatBase =
        creditTotal +
        Number(currentCreditNote?.surcharge || 0) +
        Number(currentCreditNote?.shipping || 0);

    if (!loadAttempted) {
        return (
            <div className="odv2">
                <NavbarV2 />
                <ListPageLoadingV2 />
            </div>
        );
    }

    if (!currentCreditNote || !order) {
        return (
            <div className="odv2">
                <NavbarV2 />
                <div className="odv2-content" style={{ paddingTop: '24px' }}>
                    <ListEmptyStateV2
                        icon={FaFileAlt}
                        title="Credit note not found"
                        description="This order has no credit note, or the link is invalid."
                        secondaryAction={{
                            label: 'Back to credit notes',
                            onClick: () => navigate('/creditnotes'),
                        }}
                    />
                </div>
            </div>
        );
    }

    if (printing) {
        return (
            <div className="order-detail order-detail-printing mb-50">
                <PrintHero />
                <div className="order-detail-toolbar">
                    {order && admin && (
                        <div>
                            <h4 onClick={() => navigate(`/order/${params.id}`)} className="mt-5 mb-0">
                                Order #{old ? order.id : order.orderNumber}
                            </h4>
                            {order.invoice && order.invoice.invoiceNumber > 0 && (
                                <h4 onClick={() => navigate(`/invoice/${params.id}`)} className="mt-5 mb-0">
                                    Invoice #{order.invoice.invoiceNumber}
                                </h4>
                            )}
                            <h4 className="mt-5 mb-0">Credit note #{currentCreditNote.creditNoteNumber}</h4>
                        </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center' }} className="mt-20" />
                </div>

                <div className="order-detail-toolbar-info mt-0 noshadow">
                    <div className="input-group">
                        <label className="printing-text">Credit Note</label>
                        <p>{currentCreditNote?.creditNoteNumber}</p>
                    </div>
                    <div className="input-group">
                        <label className="printing-text">Credit Note Date</label>
                        <p>
                            {new Intl.DateTimeFormat('en-GB', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                            }).format(new Date(currentCreditNote.date))}
                        </p>
                    </div>
                    <div className="input-group">
                        <label className="printing-text">Credit Note Total</label>
                        <p>- €{formatMoney(getCombinedTotal())}</p>
                    </div>
                </div>

                <div className="order-detail-wrapper-printing">
                    <div className="cartlist-printing">
                        <CartList data={creditCartItems} showcase printing={printing} stockLoading={stockLoading} />
                    </div>
                    <div className="order-detail-price-wrapper-printing ">
                        <div className="order-detail-price mt-20 noshadow m-0 p-0">
                            <form>
                                <h4 className="mt-0 mb-10 mt-10 printing-text-xl">Credit Details</h4>
                                <div className="order-detail-price-printing">
                                    <div>
                                        <div className="input-group-printing">
                                            <label className="printing-text">Credit Total</label>
                                            <p>- €{formatMoney(creditTotal)}</p>
                                        </div>
                                        <div className="input-group-printing">
                                            <label className="printing-text">Surcharge Total</label>
                                            <p>- €{formatMoney(currentCreditNote?.surcharge)}</p>
                                        </div>
                                        <div className="input-group-printing">
                                            <label className="printing-text">Shipping Total</label>
                                            <p>- €{formatMoney(currentCreditNote?.shipping)}</p>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="input-group-printing">
                                            <label className="printing-text">VAT (18%)</label>
                                            <p>- €{formatMoney(vat18FromCombined(vatBase))}</p>
                                        </div>
                                        <div className="input-group-printing">
                                            <label className="printing-text">Combined Total</label>
                                            <p>- €{formatMoney(getCombinedTotal())}</p>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="odv2">
            <NavbarV2 />

            <div className="odv2-toolbar">
                {admin ? (
                    <div>
                        <h4 onClick={() => navigate(`/order/${params.id}`)}>
                            Order #{old ? order.id : order.orderNumber}
                        </h4>
                        {order.invoice && order.invoice.invoiceNumber > 0 && (
                            <h4
                                onClick={() => navigate(`/invoice/${params.id}`)}
                                style={{
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    marginTop: '4px',
                                    color: 'var(--v2-text-muted)',
                                }}
                            >
                                Invoice #{order.invoice.invoiceNumber}
                            </h4>
                        )}
                        <p
                            style={{
                                margin: '8px 0 0',
                                fontSize: '13px',
                                fontWeight: 700,
                                color: 'var(--v2-terracotta)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                            }}
                        >
                            Credit note #{currentCreditNote.creditNoteNumber}
                        </p>
                    </div>
                ) : (
                    <div>
                        <h4>Credit note #{currentCreditNote.creditNoteNumber}</h4>
                    </div>
                )}
                <div className="odv2-toolbar-actions">
                    {admin && (
                        <button
                            type="button"
                            className="odv2-btn"
                            onClick={handlePrintCreditNotePdf}
                            disabled={pdfGenerating}
                        >
                            {pdfGenerating ? 'PDF…' : 'Print'}
                        </button>
                    )}
                    <button
                        type="button"
                        className="odv2-btn odv2-btn--outline"
                        onClick={() => navigate(`/invoice/${params.id}`)}
                    >
                        Invoice
                    </button>
                    {admin && currentCreditNote && (
                        <button
                            type="button"
                            className="odv2-btn odv2-btn--secondary"
                            onClick={() =>
                                navigate(`/createcreditnote/${params.id}?creditNoteId=${currentCreditNote._id}`)
                            }
                        >
                            Edit credit note
                        </button>
                    )}
                    {!admin && order?.invoice?.status === 'UNPAID' && (
                        <button type="button" className="odv2-btn" onClick={pay}>
                            Pay
                        </button>
                    )}
                </div>
            </div>

            <div className="odv2-info-bar creditnote-detail-info">
                <div className="odv2-info-card">
                    <p className="odv2-info-label">Credit note #</p>
                    <p className="odv2-info-value">{currentCreditNote.creditNoteNumber}</p>
                </div>
                <div className="odv2-info-card">
                    <p className="odv2-info-label">Date</p>
                    <p className="odv2-info-value">
                        {new Intl.DateTimeFormat('en-GB', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                        }).format(new Date(currentCreditNote.date))}
                    </p>
                </div>
                <div className="odv2-info-card">
                    <p className="odv2-info-label">Credit total</p>
                    <p className="odv2-info-value" style={{ color: 'var(--v2-terracotta)' }}>
                        - €{formatMoney(getCombinedTotal())}
                    </p>
                </div>
            </div>

            <div className="odv2-content">
                {stockLoading && !printing && creditCartItems.length > 0 && (
                    <p
                        className="v2-muted"
                        style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}
                    >
                        <ClipLoader color="#C4603C" size={14} />
                        Checking stock…
                    </p>
                )}
                <CartList data={creditCartItems} showcase printing={printing} noMargin stockLoading={stockLoading} />

                <div className="odv2-card" style={{ marginTop: '20px' }}>
                    <form onSubmit={updatePayment}>
                        <h4>Credit details</h4>
                        <div className="odv2-card-grid">
                            <div>
                                <div className="odv2-field">
                                    <label>Credit total</label>
                                    <p>- €{formatMoney(creditTotal)}</p>
                                </div>
                                <div className="odv2-field">
                                    <label>Surcharge total</label>
                                    {admin ? (
                                        <p>
                                            - €{' '}
                                            <input
                                                defaultValue={formatMoney(currentCreditNote?.surcharge)}
                                                id="surcharge"
                                                name="surcharge"
                                                style={{ width: '72px' }}
                                            />
                                        </p>
                                    ) : (
                                        <p>- €{formatMoney(currentCreditNote?.surcharge)}</p>
                                    )}
                                </div>
                                <div className="odv2-field">
                                    <label>Shipping total</label>
                                    {admin ? (
                                        <p>
                                            - €{' '}
                                            <input
                                                defaultValue={formatMoney(currentCreditNote?.shipping)}
                                                id="shipping"
                                                name="shipping"
                                                style={{ width: '72px' }}
                                            />
                                        </p>
                                    ) : (
                                        <p>- €{formatMoney(currentCreditNote?.shipping)}</p>
                                    )}
                                </div>
                            </div>
                            <div>
                                <div className="odv2-field">
                                    <label>VAT (18%)</label>
                                    <p>- €{formatMoney(vat18FromCombined(vatBase))}</p>
                                </div>
                                <div className="odv2-field">
                                    <label>Combined total</label>
                                    <p style={{ fontSize: '18px', fontWeight: 800, color: 'var(--v2-terracotta)' }}>
                                        - €{formatMoney(getCombinedTotal())}
                                    </p>
                                </div>
                            </div>
                        </div>
                        {order.method && order.method !== 'vivaWallet' && (
                            <div className="odv2-field" style={{ marginTop: '12px' }}>
                                <label>Method</label>
                                <p>{order.method}</p>
                            </div>
                        )}
                        {admin && (
                            <button type="submit" className="odv2-btn" style={{ marginTop: '16px' }}>
                                Change
                            </button>
                        )}
                    </form>
                </div>

                <div className="odv2-card">
                    <h4>Billing details</h4>
                    <div className="odv2-card-grid">
                        <div>
                            <div className="odv2-field">
                                <label>Full name</label>
                                <p>{old ? order.owner?.name : order.name}</p>
                            </div>
                            <div className="odv2-field">
                                <label>Company name</label>
                                <p>{old ? order.owner?.company : order.company}</p>
                            </div>
                            <div className="odv2-field">
                                <label>VAT NO</label>
                                <p>{old ? order.owner?.VAT : order.VAT}</p>
                            </div>
                            <div className="odv2-field">
                                <label>Street address</label>
                                <p>{old ? order.owner?.street : order.street}</p>
                            </div>
                        </div>
                        <div>
                            <div className="odv2-field">
                                <label>Town / city</label>
                                <p>{old ? order.owner?.city : order.city}</p>
                            </div>
                            <div className="odv2-field">
                                <label>Post code</label>
                                <p>{old ? order.owner?.postCode : order.postCode}</p>
                            </div>
                            <div className="odv2-field">
                                <label>Phone</label>
                                <p>{old ? order.owner?.phone : order.phone}</p>
                            </div>
                            <div className="odv2-field">
                                <label>Email</label>
                                <p>{old ? order.owner?.email : order.email}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="odv2-card">
                    <h4>Delivery details</h4>
                    <div className="odv2-card-grid">
                        <div>
                            <div className="odv2-field">
                                <label>Full name</label>
                                <p>{order.deliveryname || order.deliveryName}</p>
                            </div>
                            <div className="odv2-field">
                                <label>Company name</label>
                                <p>{order.deliverycompanyName}</p>
                            </div>
                            <div className="odv2-field">
                                <label>VAT NO</label>
                                <p>{order.deliveryVAT}</p>
                            </div>
                            <div className="odv2-field">
                                <label>Street address</label>
                                <p>{order.deliverystreet || order.deliveryStreet}</p>
                            </div>
                        </div>
                        <div>
                            <div className="odv2-field">
                                <label>Town / city</label>
                                <p>{order.deliverycity || order.deliveryCity}</p>
                            </div>
                            <div className="odv2-field">
                                <label>Post code</label>
                                <p>{order.deliverypostCode || order.deliveryPostCode}</p>
                            </div>
                            <div className="odv2-field">
                                <label>Phone</label>
                                <p>{order.deliveryphone || order.deliveryPhone}</p>
                            </div>
                            <div className="odv2-field">
                                <label>Email</label>
                                <p>{order.deliveryemail || order.deliveryEmail}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreditNoteDetailV2;
